import axios from "axios";
import { OpenAI } from "openai"; 

const LS_BASE_URL = "https://linksciences.com";

const getPhysician = async (npi) => {
    try {
        const response = await axios.get(LS_BASE_URL + '/getPhysician.do', {
            params: { npi }
        });
        // console.log(response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching physician: ', error);
    }
}

const getSimilarPhysicians = async (npi) => {
    try {
        const response = await axios.get(LS_BASE_URL + '/getKSimilarPhysicians.do', {
            params: { npi }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching similar physicians: ', error);
    }
}

// https://linksciences.com/getPhysicianExpertises.do?npi=1003012444
const getPhysicianExpertises = async (npi) => {
    try {
        const response = await axios.get(LS_BASE_URL + '/getPhysicianExpertises.do', {
            params: { npi }
        });
        return response.data;
    } catch (error) {
        console.error('Error getPhysicianExpertises: ', error);
    }
}

// https://linksciences.com/getPhysicianTrials.do?npi=1003012444
const getPhysicianTrials = async (npi) => {
    try {
        const response = await axios.get(LS_BASE_URL + '/getPhysicianTrials.do', {
            params: { npi }
        });
        return response.data;
    } catch (error) {
        console.error('Error getPhysicianTrials: ', error);
    }
}

// http://linksciences.com/getNpiDkwsFromSimilarity.do?npi=1184654253
const getNpiDkwsFromSimilarity = async (npi) => {
    try {
        const response = await axios.get(LS_BASE_URL + '/getNpiDkwsFromSimilarity.do', {
            params: { npi }
        });
        return response.data;
    } catch (error) {
        console.error('Error getNpiDkwsFromSimilarity: ', error);
    }
}

// https://www.linksciences.com/getNeighbours.do?dkw=Pancreatic+Neoplasms
const getNeighbours = async (dkw) => {
    try {
        const response = await axios.get(LS_BASE_URL + '/getNeighbours.do', {
            params: { dkw }
        });
        return response.data;
    } catch (error) {
        console.error('Error getNeighbours: ', error);
    }
}

/**
 * Search for physicians whose full name contains the input string
 * @param {string} inputString - The string to search for in physician names
 * @returns {Promise<Array>} - Array of objects containing NPI and full_name
 */
const ES_BASE_URL = process.env.NODE_ENV === "production"
    ? "http://129.80.83.28:9200"
    : "";

const wildSearchPhysicians = async (inputString) => {
    try {
        // Create Elasticsearch query to search for the input string in full_name field
        const query = {
            query: {
                wildcard: {
                    "full_name.keyword": `*${inputString.toUpperCase()}*`
                }
            },
            _source: ["npi", "full_name"] // Only return these fields
        };

        // Use full ES URL in production, proxy (relative URL) in development
        const url = `${ES_BASE_URL}/ls_npi_names/_search`;
        const response = await axios.post(url, query, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Extract and return just the hits array with physicians matching the search
        if (response.data && response.data.hits && response.data.hits.hits) {
            return response.data.hits.hits.map(hit => ({
                ...hit._source,
                npi: hit._id // Include the _id field
            }));
        }
        return [];
    } catch (error) {
        console.error('Error in wildSearchPhysicians: ', error);
        return [];
    }
}

// Generate roast using Bedrock AWS
// bedrock.aws.region=us-west-2
// bedrock.aws.accessKey=AKIAZI2LCYXJKST4CVG4
// bedrock.aws.secretKey=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
// bedrock.aws.modelId=meta.llama3-1-70b-instruct-v1:0
const generateRoastByBedrockAWS = async (physicianData, expertiseData, trialsData, temp ) => {
    try {
        const response = await axios.post('https://bedrock.aws.amazon.com/v1/models/meta.llama3-1-70b-instruct-v1:0/completions', {
            messages: [
                {
                    role: 'user',
                    content: `Create a humorous but professional roast for Dr. ${physicianData.frst_nm} ${physicianData.lst_nm}, 
                    a ${physicianData.pri_spec} physician.
                    
                    Additional information:
                    - Works at: ${physicianData.org_nm || 'Unknown facility'}
                    - Location: ${physicianData.cty}, ${physicianData.st}
                    - Key expertise areas: ${expertiseData && expertiseData.length > 0 ? 
                        expertiseData.slice(0, 5).map(e => e.meshName).join(', ') : 'Unknown'}
                    - Clinical trials involvement: ${trialsData && trialsData.length > 0 ? 
                        trialsData.length + ' trials' : 'No known trials'}
                    
                    Make the roast clever, witty, and tailored to their specialty and background.
                    Keep it professional enough that it could be shared in a medical conference.
                    Maximum length: 150 words.`
                }
            ],
            max_tokens: 300,
            temperature: temp,
        });
        return response.data.choices[0].message.content.trim();
    } catch (error) {
        console.error('Error generating roast: ', error);
        return "Sorry, I couldn't generate a roast at this time. The doctor must be too impressive to roast!";
    }
}


// openai key:
//  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
// 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
// Generate roast using OpenAI GPT-4o
const generateRoast = async (physicianData, expertiseData, trialsData, temp ) => {
    // console.log("physicianData", physicianData);
    // console.log("expertiseData", expertiseData);
    // console.log("trialsData", trialsData);

    const physicianBasicInfo = `Dr. ${physicianData.frst_nm} ${physicianData.lst_nm}, 
    a ${physicianData.pri_spec} physician.
    
    Additional information:
    - Works at: ${physicianData.org_nm || 'Unknown facility'}
    - Location: ${physicianData.cty}, ${physicianData.st}
    
    `;
    
    try {
        // Initialize the OpenAI client
        const openai = new OpenAI({
            apiKey: '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',
            dangerouslyAllowBrowser: true
        });

        // Create a prompt for a sentence with physician expertise like this:
        // Radiation Oncologist, Multidisciplinary Maestro, and Certified Overachiever
        const prompt0 = `
            Create a sentence with physician expertise like this:
            Radiation Oncologist, Multidisciplinary Maestro, and Certified Overachiever  
        `;

        const response0 = await openai.chat.completions.create({
            model: "gpt-4o",
            messages: [
                {
                    role: "system",
                    content: "You are a witty medical comedian who creates personalized, good-natured roasts for physicians."
                },
                {
                    role: "user",
                    content: physicianBasicInfo + prompt0
                }
            ],
            max_tokens: 15,
            temperature: temp,
        });

        // Create a prompt for roast with physician information
        const prompt1 = `
            Create a humorous but professional roast for Dr. ${physicianData.frst_nm} ${physicianData.lst_nm}, 
            a ${physicianData.pri_spec} physician.
            
            Additional information:
            - Works at: ${physicianData.org_nm || 'Unknown facility'}
            - Location: ${physicianData.cty}, ${physicianData.st}
            - Key expertise areas: ${expertiseData && expertiseData.length > 0 ? 
                expertiseData.slice(0, 5).map(e => e.meshName).join(', ') : 'Unknown'}
            - Clinical trials involvement: ${trialsData && trialsData.length > 0 ? 
                trialsData.length + ' trials' : 'No known trials'}
            
            Make the roast clever, witty, and tailored to their specialty and background.
            Keep it professional enough that it could be shared in a medical conference.
            Just roast itself, no need greetings to the audience.
            Maximum length: 150 words.
        `;

        // Call OpenAI API using the client library
        const   response1 = await openai.chat.completions.create({
            model: "gpt-4o",
            messages: [
                {
                    role: "system",
                    content: "You are a witty medical comedian who creates personalized, good-natured roasts for physicians."
                },
                {
                    role: "user",
                    content: physicianBasicInfo + prompt1
                }
            ],
            max_tokens: 300,
            temperature: temp,
        });


        // Create a prompt for summary of Clinical Capability with physician information
        const prompt2 = `
            Create a summary of Clinical Capability for Dr. ${physicianData.frst_nm} ${physicianData.lst_nm}, 
            a ${physicianData.pri_spec} physician.
            
            Additional information:
            - Works at: ${physicianData.org_nm || 'Unknown facility'}
            - Location: ${physicianData.cty}, ${physicianData.st}
            - Key expertise areas: ${expertiseData && expertiseData.length > 0 ? 
                expertiseData.slice(0, 5).map(e => e.meshName).join(', ') : 'Unknown'}
            - Clinical trials involvement: ${trialsData && trialsData.length > 0 ? 
                trialsData.length + ' trials' : 'No known trials'}
            
            Make the summary concise and informative.
            Maximum length: 150 words.
        `;

        // Call OpenAI API using the client library
        const   response2 = await openai.chat.completions.create({
            model: "gpt-4o",
            messages: [
                {
                    role: "system",
                    content: "You are a medical expert who creates concise and informative summaries for physicians."
                },
                {
                    role: "user",
                    content: physicianBasicInfo + prompt2
                }
            ],
            max_tokens: 300,
            temperature: temp,
        });

        // Create a prompt for very spicy roast with physician information
        const prompt3 = `
            Create a humorous but professional roast for Dr. ${physicianData.frst_nm} ${physicianData.lst_nm}, 
            a ${physicianData.pri_spec} physician.
            
            Additional information:
            - Works at: ${physicianData.org_nm || 'Unknown facility'}
            - Location: ${physicianData.cty}, ${physicianData.st}
            - Key expertise areas: ${expertiseData && expertiseData.length > 0 ? 
                expertiseData.slice(0, 5).map(e => e.meshName).join(', ') : 'Unknown'}
            - Clinical trials involvement: ${trialsData && trialsData.length > 0 ? 
                trialsData.length + ' trials' : 'No known trials'}
            
            Make the roast spicy, clever, witty, and tailored to their specialty and background.
            Keep it professional enough that it could be shared in a medical conference.
            Just roast itself, no need greetings to the audience.
            Maximum length: 100 words.
        `;

        // Call OpenAI API using the client library
        const   response3 = await openai.chat.completions.create({
            model: "gpt-4o",
            messages: [
                {
                    role: "system",
                    content: "You are a witty medical comedian who creates personalized, good-natured roasts for physicians."
                },
                {
                    role: "user",
                    content: physicianBasicInfo + prompt3
                }
            ],
            max_tokens: 200,
            temperature: temp,
        });

        // Create a prompt for summary of Care Compatibility with physician information
        const prompt4 = `
            Create a summary of Care Compatibility for Dr. ${physicianData.frst_nm} ${physicianData.lst_nm}, 
            a ${physicianData.pri_spec} physician.
            
            Additional information:
            - Works at: ${physicianData.org_nm || 'Unknown facility'}
            - Location: ${physicianData.cty}, ${physicianData.st}
            - Key expertise areas: ${expertiseData && expertiseData.length > 0 ? 
                expertiseData.slice(0, 5).map(e => e.meshName).join(', ') : 'Unknown'}
            - Clinical trials involvement: ${trialsData && trialsData.length > 0 ? 
                trialsData.length + ' trials' : 'No known trials'}
            
            Make the summary concise and informative.
            Maximum length: 80 words.
        `;

        // Call OpenAI API using the client library
        const   response4 = await openai.chat.completions.create({
            model: "gpt-4o",
            messages: [
                {
                    role: "system",
                    content: "You are a medical expert who creates concise and informative summaries for physicians."
                },
                {
                    role: "user",
                    content: physicianBasicInfo + prompt4
                }
            ],
            max_tokens: 150,
            temperature: temp,
        });

        // Return the generated roast
        return {
            tagline:response0.choices[0].message.content.trim(), 
            roast:response1.choices[0].message.content.trim(), 
            summary:response2.choices[0].message.content.trim(),
            spicyRoast:response3.choices[0].message.content.trim(),
            careCompatibility:response4.choices[0].message.content.trim()
        };
    } catch (error) {
        console.error('Error generating roast: ', error);
        return "Sorry, I couldn't generate a roast at this time. The doctor must be too impressive to roast!";
    }



    
};

export { getPhysician, getNpiDkwsFromSimilarity, generateRoastByBedrockAWS, getSimilarPhysicians, getPhysicianExpertises, getPhysicianTrials, getNeighbours, generateRoast, wildSearchPhysicians }